/**
 * Go Sport AI Coach — Utilise le service IA existant (Gemini) pour :
 * - Générer des séances HIIT personnalisées
 * - Analyser la progression et donner des conseils
 * - Créer des plans nutrition adaptés
 */

import aiService from '../../../services/aiService';
import { SPORT_LEVELS, HIIT_RATIOS } from '../data/levels';

// ========== Prompts ==========

const PROMPT_CUSTOM_SESSION = `Tu es un coach sportif TDAH-friendly en français.
Génère une séance HIIT personnalisée basée sur le profil suivant :

Niveau sportif: {level}/10 ({levelLabel})
Zone ciblée: {zone}
Durée souhaitée: {duration} min
Objectifs: {objectives}
Limitations/blessures: {limitations}
Temps travail: {work}s / Temps repos: {rest}s
Nombre de cycles: {cycles}

Réponds UNIQUEMENT en JSON valide (pas de markdown) :
{
  "exercises": [
    {
      "name": "Nom de l'exercice",
      "tip": "Conseil de forme en 1 ligne",
      "zone": "upper" ou "lower" ou "full" ou "cardio",
      "intensity": 1 à 10
    }
  ],
  "warmup": "Conseil d'échauffement (1-2 phrases)",
  "motivation": "Message motivant TDAH-friendly court",
  "focusTip": "Conseil TDAH pour rester concentré pendant la séance"
}

Règles :
- Exactement {cycles} exercices
- Adaptés au niveau {level} (pas trop facile, pas trop dur)
- Éviter les mouvements qui touchent les zones blessées : {limitations}
- Varier les groupes musculaires pour la zone {zone}
- Exercices faisables à la maison sans matériel (sauf niveaux 7+)
- Noms en français`;

const PROMPT_PROGRESSION_ANALYSIS = `Tu es un coach sportif TDAH-friendly et bienveillant en français.
Analyse la progression sportive suivante et donne des conseils personnalisés.

Profil :
- Niveau actuel: {level}/10 ({levelLabel})
- Objectifs: {objectives}
- Streak actuel: {streak} jours (meilleur: {bestStreak})

Historique des 10 dernières séances :
{sessionsJSON}

Statistiques globales :
- Séances totales: {totalSessions}
- Durée totale: {totalMinutes} min
- Calories totales: {totalCalories} kcal
- Note moyenne: {avgRating}/5

Réponds UNIQUEMENT en JSON valide :
{
  "analysis": "Analyse courte (2-3 phrases) de la progression",
  "strengths": ["point fort 1", "point fort 2"],
  "improvements": ["axe d'amélioration 1"],
  "nextGoal": "Objectif concret pour la semaine prochaine",
  "motivationTip": "Conseil motivation adapté TDAH",
  "levelAdvice": "Conseil sur le niveau actuel (rester, monter, ajuster ?)"
}

Règles TDAH :
- Toujours commencer par un encouragement
- Pas de culpabilisation si streak cassé
- Objectifs concrets et atteignables (micro-objectifs)
- Célébrer les petites victoires`;

const PROMPT_NUTRITION_PLAN = `Tu es un nutritionniste sportif TDAH-friendly en français.
Crée un plan nutrition adapté pour le profil suivant :

Niveau sportif: {level}/10
Objectifs: {objectives}
Traitement TDAH (stimulants): {hasMedication}
Moment: {timing} (pré-séance ou post-séance)

Réponds UNIQUEMENT en JSON valide :
{
  "meals": [
    {
      "timing": "2h avant" ou "30min avant" ou "Immédiat" ou "Dans 2h",
      "suggestion": "Description du repas/snack",
      "emoji": "emoji approprié",
      "quick": true ou false
    }
  ],
  "hydration": "Conseil hydratation personnalisé",
  "supplements": [
    {
      "name": "Nom du complément",
      "dosage": "Dosage recommandé",
      "reason": "Pourquoi ce complément pour ce profil"
    }
  ],
  "tdahTip": "Conseil nutrition spécifique TDAH (interaction médicaments, etc.)"
}

Règles :
- Repas simples et rapides à préparer (TDAH = difficulté planification)
- Si traitement stimulant : attention à la caféine, hydratation++
- Maximum 3 suggestions par timing
- Suggestions réalistes et faciles à trouver`;

// ========== Service ==========

class GoSportAIService {
  /**
   * Générer une séance personnalisée par IA
   */
  async generateCustomSession(profile, sessionConfig) {
    if (!aiService.isAvailable()) return null;

    const level = sessionConfig.level || 1;
    const levelInfo = SPORT_LEVELS.find(l => l.level === level) || SPORT_LEVELS[0];
    const ratio = HIIT_RATIOS.find(r => r.level === level) || HIIT_RATIOS[0];

    const prompt = PROMPT_CUSTOM_SESSION
      .replace(/\{level\}/g, String(level))
      .replace('{levelLabel}', levelInfo.label)
      .replace('{zone}', sessionConfig.zone || 'full')
      .replace('{duration}', String(sessionConfig.duration || 30))
      .replace('{objectives}', (profile.objectives || []).join(', ') || 'bien-être général')
      .replace(/\{limitations\}/g, (profile.limitations || []).join(', ') || 'aucune')
      .replace('{work}', String(ratio.work))
      .replace('{rest}', String(ratio.rest))
      .replace(/\{cycles\}/g, String(sessionConfig.totalCycles || ratio.cycles));

    try {
      const response = await aiService.callGemini(prompt);
      const clean = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const jsonMatch = clean.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;

      const result = JSON.parse(jsonMatch[0]);

      // Map exercises to the format expected by the app
      const exercises = (result.exercises || []).map((ex, i) => ({
        id: `ai_${ex.name.toLowerCase().replace(/\s+/g, '_')}_${i}`,
        name: ex.name,
        tip: ex.tip || '',
        zone: ex.zone || 'full',
        levels: [Math.max(1, level - 1), level, Math.min(10, level + 1)],
        animation: 'squat', // fallback animation
      }));

      return {
        exercises,
        warmup: result.warmup || '',
        motivation: result.motivation || '',
        focusTip: result.focusTip || '',
        isAIGenerated: true,
      };
    } catch (error) {
      console.warn('Go Sport AI session generation error:', error);
      return null;
    }
  }

  /**
   * Analyser la progression et donner des conseils
   */
  async analyzeProgression(profile, sessions, stats, streak) {
    if (!aiService.isAvailable()) return null;

    const level = stats?.level || 1;
    const levelInfo = SPORT_LEVELS.find(l => l.level === level) || SPORT_LEVELS[0];

    const recentSessions = (sessions || []).slice(-10).map(s => ({
      date: s.date,
      level: s.level,
      duration: Math.round((s.duration || 0) / 60) + ' min',
      calories: s.caloriesEstimated || 0,
      rating: s.rating || '—',
      completed: s.exercisesCompleted || 0,
    }));

    const prompt = PROMPT_PROGRESSION_ANALYSIS
      .replace('{level}', String(level))
      .replace('{levelLabel}', levelInfo.label)
      .replace('{objectives}', (profile.objectives || []).join(', ') || 'bien-être')
      .replace('{streak}', String(streak?.current || 0))
      .replace('{bestStreak}', String(streak?.best || 0))
      .replace('{sessionsJSON}', JSON.stringify(recentSessions, null, 2))
      .replace('{totalSessions}', String(stats?.totalSessions || 0))
      .replace('{totalMinutes}', String(Math.round((stats?.totalDuration || 0) / 60)))
      .replace('{totalCalories}', String(stats?.totalCalories || 0))
      .replace('{avgRating}', String(stats?.avgRating || 0));

    try {
      const response = await aiService.callGemini(prompt);
      const clean = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const jsonMatch = clean.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.warn('Go Sport AI progression analysis error:', error);
      return null;
    }
  }

  /**
   * Générer un plan nutrition personnalisé
   */
  async generateNutritionPlan(profile, level, timing = 'pre') {
    if (!aiService.isAvailable()) return null;

    const prompt = PROMPT_NUTRITION_PLAN
      .replace('{level}', String(level))
      .replace('{objectives}', (profile.objectives || []).join(', ') || 'bien-être')
      .replace('{hasMedication}', profile.hasMedication ? 'Oui' : 'Non')
      .replace('{timing}', timing === 'pre' ? 'pré-séance' : 'post-séance');

    try {
      const response = await aiService.callGemini(prompt);
      const clean = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const jsonMatch = clean.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.warn('Go Sport AI nutrition plan error:', error);
      return null;
    }
  }
}

const goSportAI = new GoSportAIService();
export default goSportAI;
